import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { UserNotification } from '../../../../models/userNotification';
import { formatDate } from '../../../../utils/date';
import { formatDateLocale } from '../../../../utils/formatDateLocale';

import { getClub } from '../../../../service/clubs';
import { getUser } from '../../../../service/user';
import { markAsRead } from '../../../../service/userNotification';

import ClubImage from '../../../../components/ClubImage/ClubImage';
import UserImage from '../../../../components/UserImage/UserImage';
import Icon from '../../../../components/Icon/Icon';

import styles from './NotificationItem.module.scss';
import { main_color } from '../../../../styles/_variables';
import { useEffect, useState } from 'react';

interface PropsNotification {
  notification: UserNotification;
}

type Props = Pick< PropsNotification, 'notification'>
& {
  refetchData: () => void
};

const NotificationItem = ({ notification, refetchData }: Props) => {

  const navigate = useNavigate();
  const formattedDate = formatDate(notification.createdAt);
  const { clubId, creatorUserId, type, data} = notification;
  const [details, setDetails] = useState(null);

  const {data:club} = useQuery(
    ['club', clubId],
    () => {
      if (clubId) {
        return getClub(clubId)
      }
      return null;      
    },
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  const {data:creatorUser} = useQuery(
    ['notification', 'user', creatorUserId],
    () => getUser(creatorUserId),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  const getNotificationDetails = () => {
    if (type === 'liked_comment' && data?.bookName) {
      return (
        <span className={styles.link}
          onClick={() => navigate(`/book/local/${notification.data?.bookId}`)}
          title="Acessar livro"
        >
          Livro: <strong>{data?.bookName}</strong>
        </span>
      );
    }

    if ((type === 'forum_topic_approved' || type === 'new_forum_topic') && data?.forumTopicName) {
      return (
        <span className={styles.link}
        onClick={() => navigate(`/club/${clubId}/${club?.name}/forum/${data?.forumId}/topic/${data?.forumTopicId}`)}
          title="Acessar tópico"
        >
          Tópico do fórum: <strong>{data?.forumTopicName}</strong>
        </span>
      );
    }

    if (type === 'forum_topic_approval' && data?.forumTopicId) {
      return (
        <span className={styles.link}
          onClick={() => navigate(`/club/${clubId}/${club?.name}/forum/`)}
          title="Acessar ao fórum do clube"
        >
          Acesse ao fórum para visualizar o tópico!
        </span>
      );
    }

    if ((type === 'meet_election' || type === 'book_election') && data?.electionId) {
      const currentDate = new Date();
      const finalDate = new Date(data.finalDate);
      const formattedFinalDate = formatDateLocale(data?.finalDate);
      const text = (currentDate > finalDate)
        ? `Votação encerrada em` : `Votação disponível até`;

      return (
        <span className={styles.link}
          onClick={() => navigate(`/club/${clubId}/election/${data?.electionId}/candidates/meet`)}
          title="Acessar a votação"
        >
          {text} <strong>{formattedFinalDate}</strong>
        </span>
      );
    }

    return undefined;
  };

  useEffect(() => {
    const details_ = getNotificationDetails();
    setDetails(details_);
  }, [notification]);

  const isClubNotification =
    type === 'forum_topic_approved' ||
    type === 'new_forum_topic' ||
    type === 'book_election' ||
    type === 'meet_election' ||
    type === 'scheduled_meeting' ||
    type === 'accepted_member' ||
    type === 'promoted_mediator';

  const isUserNotification =
    type === 'forum_topic_approval' ||
    type === 'liked_comment' ||
    type === 'membership_request' ||
    type === 'new_member';

  const markNotificationAsRead = async () => {
    await markAsRead(notification.id);
    refetchData();
  };

  return (
    <div className={styles.notification} key={'notification_' + notification.id}>
      <div className={styles.notificationHeader}>
        <div className={styles.date}> {formattedDate} </div>
      </div>
      <div className={styles.notificationContainer}>
        {isUserNotification && (
          <div className={styles.profile} onClick={() => navigate(`/user/${creatorUserId}`)}>
            <UserImage style={styles.circleUser} path={creatorUser?.pathPhoto} name={creatorUser?.fullname} />
            <span className={styles.name}>{creatorUser?.fullname}</span>
          </div>
        )}
        {isClubNotification && clubId && (
          <div className={styles.profile} onClick={() => navigate(`/club/${clubId}/${club?.name}`)}>
            <ClubImage clubName={club?.name} path={club?.pathPhoto} width={35} height={35} />
            <span className={styles.name}>{club?.name}</span>
          </div>
        )}
      </div>
      <div className={styles.content}>
        <span>{notification?.content}</span> 
        {details && (
          <>
            {details}
          </>
        )}
      </div>
      <div className={styles.isReadIcon}>
        {notification.isRead ? (
            <Icon icon="open-envelope" color={main_color} />
          ) : (
            <button className={styles.markAsReadButton} onClick={markNotificationAsRead}>
              <Icon icon="closed-envelope" color={main_color} />
            </button>
          )
        }
      </div>
    </div>
  );
};

export default NotificationItem;